import { useMemo } from 'react';
import { useAuthStore } from '../auth/authStore';
import { useLocationStore } from './locationStore';
import { matchCoreLocation } from './resolveCoreLocation';
import { useCoreLocations } from './useCoreLocations';
import type { Location } from './types';

export function useSelectedLocation(): Location | undefined {
  const accountLocations = useAuthStore((s) => s.locations);
  const activeAccessLocation = useAuthStore((s) => s.access?.location);
  const accessLocationAscendId = useAuthStore((s) => s.access?.location_ascend_id);
  const selectedUuid = useLocationStore((s) => s.selectedLocationUuid);
  const { data: coreLocations } = useCoreLocations();

  return useMemo(() => {
    const targetId = activeAccessLocation ?? selectedUuid;
    const accountLocation =
      accountLocations.find((loc) => loc.id === targetId) ?? accountLocations[0];
    if (!accountLocation) return undefined;

    const matchOptions = {
      accountLocationCount: accountLocations.length,
      ...(accountLocation.id === activeAccessLocation && accessLocationAscendId !== undefined
        ? { accessLocationAscendId }
        : {}),
    };
    const coreMatch = matchCoreLocation(accountLocation, coreLocations ?? [], matchOptions);

    const location: Location = {
      id: accountLocation.id,
      name: accountLocation.name,
      ascend_id: accountLocation.ascend_id,
    };

    if (coreMatch) {
      location.legacy_id = coreMatch.id;
      if (coreMatch.city) location.city = coreMatch.city;
      if (coreMatch.state) location.state = coreMatch.state;
      if (coreMatch.phone) location.phone = coreMatch.phone;
      if (coreMatch.email) location.email = coreMatch.email;
      if (coreMatch.full_address) location.full_address = coreMatch.full_address;
    }

    return location;
  }, [accessLocationAscendId, accountLocations, activeAccessLocation, coreLocations, selectedUuid]);
}
